import React, { useState, useEffect, useRef } from 'react';
import ContentCard from './ContentCard';
import ContentManager from './ContentManager';
import SessionManager from './SessionManager';
import HeroConfigModal from './HeroConfigModal';
import { Play, Clock, Users, Star, Search, Filter, Settings, Plus, Edit, Trash2, ArrowRight, ArrowLeft, ArrowUp, ArrowDown, Sparkles, VolumeX } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { Button } from './ui/button';
import { 
  loadSessions, 
  createSession, 
  updateSession, 
  deleteSession,
  loadContentItems,
  createContentItem,
  updateContentItem,
  deleteContentItem,
  loadHeroConfig,
  saveHeroConfig,
  HeroConfig,
  ContentSession as DBSession,
  ContentItem as DBContentItem
} from '../helpers/contentHelpers';
import { extractYouTubeVideoId } from '../helpers/videoHelpers';

interface ContentItem {
  id: string;
  title: string;
  description: string;
  image: string;
  progress: number;
  isCompleted: boolean;
  videoCount: string;
  tags?: string[];
}

interface ContentSession {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  contents: ContentItem[];
  order: number;
}

interface HomePageProps {
  onOpenContent?: (contentId: string) => void;
  onNavigate?: (section: string) => void;
}

// Retorna URL de embed com autoplay MUTED (sem controles)
const getMutedEmbedUrl = (url: string): string => {
  if (!url) return '';

  // YouTube
  let regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  let match = url.match(regExp);
  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&enablejsapi=1`;
  }

  // Vimeo
  regExp = /vimeo\.com\/([0-9]+)/;
  match = url.match(regExp);
  if (match && match[1]) {
    return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&controls=0&playsinline=1`;
  }

  return url;
};

// Retorna URL de embed com som E controles ativos
const getActiveEmbedUrl = (url: string): string => {
  if (!url) return '';

  // YouTube
  let regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  let match = url.match(regExp);
  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}?autoplay=1&mute=0&controls=1&rel=0&playsinline=1`;
  }

  // Vimeo
  regExp = /vimeo\.com\/([0-9]+)/;
  match = url.match(regExp);
  if (match && match[1]) {
    return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=0&controls=1&playsinline=1`;
  }

  return url;
};

const isDirectVideo = (url: string) => {
  if (!url) return false;
  const cleanUrl = url.split('?')[0].toLowerCase();
  return cleanUrl.endsWith('.mp4') || cleanUrl.endsWith('.webm') || cleanUrl.endsWith('.ogg');
};

// ─── VSL Player com autoplay mudo + click-to-unmute ─────────────────────────
interface VSLPlayerProps {
  url: string;
}

const VSLPlayer: React.FC<VSLPlayerProps> = ({ url }) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reset ao mudar de URL (ex: admin atualiza o vídeo)
  useEffect(() => {
    setHasInteracted(false);
  }, [url]);

  const handleUnmute = () => {
    setHasInteracted(true);
    // Para vídeo direto, desmutar via ref
    if (isDirectVideo(url) && videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.controls = true;
      videoRef.current.play().catch(() => {});
    }
  };

  if (!url) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        Nenhum vídeo configurado
      </div>
    );
  }

  if (isDirectVideo(url)) {
    return (
      <div className="relative w-full h-full">
        <video
          ref={videoRef}
          src={url}
          autoPlay
          muted={!hasInteracted}
          loop={!hasInteracted}
          playsInline
          controls={hasInteracted}
          className="w-full h-full object-cover"
        />
        {!hasInteracted && (
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer group z-10"
            onClick={handleUnmute}
          >
            <div className="bg-black/40 backdrop-blur-sm rounded-full p-5 border border-white/20 group-hover:bg-black/60 group-hover:scale-110 transition-all duration-300 shadow-2xl">
              <VolumeX className="w-10 h-10 text-white drop-shadow-lg" />
            </div>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/10">
              Clique para ativar o som
            </div>
          </div>
        )}
      </div>
    );
  }

  // YouTube / Vimeo via iframe
  return (
    <div className="relative w-full h-full">
      <iframe
        key={hasInteracted ? 'active' : 'muted'}
        src={hasInteracted ? getActiveEmbedUrl(url) : getMutedEmbedUrl(url)}
        title="VSL"
        className="w-full h-full border-none"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      />
      {!hasInteracted && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer group z-10"
          onClick={handleUnmute}
        >
          <div className="bg-black/40 backdrop-blur-sm rounded-full p-5 border border-white/20 group-hover:bg-black/60 group-hover:scale-110 transition-all duration-300 shadow-2xl">
            <VolumeX className="w-10 h-10 text-white drop-shadow-lg" />
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-4 py-1.5 rounded-full border border-white/10 whitespace-nowrap">
            Clique para ativar o som
          </div>
        </div>
      )}
    </div>
  );
};

const HomePage: React.FC<HomePageProps> = ({ onOpenContent, onNavigate }) => {
  const { user, isAdmin } = useAuth();
  const { showToast } = useToast();
  
  // Estados para gerenciamento de sessões
  const [showSessionManager, setShowSessionManager] = useState(false);
  const [editingSession, setEditingSession] = useState<ContentSession | null>(null);
  const [deletingSession, setDeletingSession] = useState<ContentSession | null>(null);
  
  // Estados para gerenciamento de conteúdo
  const [showContentManager, setShowContentManager] = useState(false);
  const [editingContent, setEditingContent] = useState<ContentItem | null>(null);
  const [deletingContent, setDeletingContent] = useState<ContentItem | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  
  // Estados para dados do banco
  const [sessions, setSessions] = useState<ContentSession[]>([]);
  const [loading, setLoading] = useState(true);

  // Estados para Hero Config
  const [heroConfig, setHeroConfig] = useState<HeroConfig | null>(null);
  const [showHeroConfigModal, setShowHeroConfigModal] = useState(false);

  // Carregar dados do banco ao montar ou quando o usuário mudar
  useEffect(() => {
    loadDataFromDB();
    fetchHeroConfig();
  }, [user?.id]);

  const fetchHeroConfig = async () => {
    const config = await loadHeroConfig();
    setHeroConfig(config);
  };

  const handleSaveHeroConfig = async (updatedConfig: HeroConfig) => {
    const success = await saveHeroConfig(updatedConfig);
    if (success) {
      setHeroConfig(updatedConfig);
      showToast({
        type: 'success',
        title: 'Hero atualizada',
        message: 'As configurações da Hero foram salvas com sucesso!'
      });
    } else {
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao salvar as configurações da Hero'
      });
    }
    return success;
  };

  const loadDataFromDB = async () => {
    try {
      setLoading(true);
      
      // Carregar sessões
      const dbSessions = await loadSessions();
      
      // Carregar todas as relações de vídeos ativos (publicados)
      const { data: allVideos, error: videosError } = await supabase
        .from('content_videos')
        .select('id, content_id, thumbnail_url, youtube_url')
        .eq('is_published', true);

      if (videosError) throw videosError;

      // Carregar progresso de vídeos assistidos se o usuário estiver logado
      let completedVideoIds: string[] = [];
      if (user) {
        const { data: progressData, error: progressError } = await supabase
          .from('user_video_progress')
          .select('video_id')
          .eq('user_id', user.id)
          .eq('is_completed', true);
        
        if (!progressError && progressData) {
          completedVideoIds = progressData.map(p => p.video_id);
        }
      }
      
      // Carregar conteúdos para cada sessão
      const sessionsWithContent = await Promise.all(
        dbSessions.map(async (session) => {
          const contents = await loadContentItems(session.id);
          return {
            id: session.id,
            title: session.title,
            subtitle: session.subtitle,
            description: session.description,
            color: session.color,
            order: session.order_index,
            contents: contents.map(content => {
              // Filtrar os vídeos ativos deste conteúdo
              const contentVideos = allVideos?.filter(v => v.content_id === content.id) || [];
              const totalVideos = contentVideos.length;
              
              // Contar quantos desses vídeos foram concluídos pelo usuário
              const completedCount = contentVideos.filter(v => completedVideoIds.includes(v.id)).length;
              
              // Calcular progresso
              const progressPct = totalVideos > 0 ? Math.round((completedCount / totalVideos) * 100) : 0;
              const isCompleted = totalVideos > 0 && progressPct === 100;
              
              const videoCountText = totalVideos === 1 ? '1 vídeo' : `${totalVideos} vídeos`;

              // Tentar resolver imagem com fallback
              let resolvedImage = content.image_url;
              if (!resolvedImage && contentVideos.length > 0) {
                const firstVideo = contentVideos[0];
                if (firstVideo.thumbnail_url) {
                  resolvedImage = firstVideo.thumbnail_url;
                } else if (firstVideo.youtube_url) {
                  const ytid = extractYouTubeVideoId(firstVideo.youtube_url);
                  if (ytid) {
                    resolvedImage = `https://img.youtube.com/vi/${ytid}/hqdefault.jpg`;
                  }
                }
              }

              return {
                id: content.id,
                title: content.title,
                description: content.description,
                image: resolvedImage || '',
                progress: progressPct,
                isCompleted: isCompleted,
                videoCount: videoCountText,
                tags: content.tags
              };
            })
          };
        })
      );

      setSessions(sessionsWithContent);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao carregar dados do servidor'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContentClick = (contentId: string) => {
    if (onOpenContent) {
      onOpenContent(contentId);
      return;
    }
    console.log(`Abrindo categoria: ${contentId}`);
    showToast({
      type: 'info',
      title: 'Navegação',
      message: `Redirecionando para a categoria: ${contentId}`
    });
  };

  const handleEditContent = (content: ContentItem, sessionId: string) => {
    setEditingContent(content);
    setSelectedSessionId(sessionId);
    setShowContentManager(true);
  };

  const handleDeleteContent = (content: ContentItem, sessionId: string) => {
    setDeletingContent(content);
    setSelectedSessionId(sessionId);
  };

  const handleEditSession = (session: ContentSession) => {
    setEditingSession(session);
    setShowSessionManager(true);
  };

  const handleDeleteSession = (session: ContentSession) => {
    setDeletingSession(session);
  };

  const handleAddContentToSession = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    setEditingContent(null);
    setShowContentManager(true);
  };

  const handleAddNewSession = () => {
    setEditingSession(null);
    setShowSessionManager(true);
  };

  const handleSessionUpdated = async (updatedSession: ContentSession) => {
    try {
      if (editingSession) {
        // Atualizar sessão existente
        const dbSession = await updateSession(editingSession.id, {
          title: updatedSession.title,
          subtitle: updatedSession.subtitle,
          description: updatedSession.description,
          color: updatedSession.color,
          order_index: updatedSession.order
        });

        if (dbSession) {
          await loadDataFromDB();
          showToast({
            type: 'success',
            title: 'Sessão atualizada',
            message: 'A sessão foi atualizada com sucesso!'
          });
        }
      } else {
        // Adicionar nova sessão
        const newOrder = sessions.length + 1;
        const dbSession = await createSession({
          title: updatedSession.title,
          subtitle: updatedSession.subtitle,
          description: updatedSession.description,
          color: updatedSession.color,
          order_index: newOrder
        });

        if (dbSession) {
          await loadDataFromDB();
          showToast({
            type: 'success',
            title: 'Sessão criada',
            message: 'A nova sessão foi criada com sucesso!'
          });
        }
      }
    } catch (error) {
      console.error('Erro ao salvar sessão:', error);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao salvar sessão'
      });
    }

    setShowSessionManager(false);
    setEditingSession(null);
  };

  const handleContentUpdated = async (updatedContent: ContentItem) => {
    if (!selectedSessionId) return;

    try {
      if (editingContent) {
        // Atualizar conteúdo existente
        const dbContent = await updateContentItem(editingContent.id, {
          title: updatedContent.title,
          description: updatedContent.description,
          image_url: updatedContent.image,
          video_count: updatedContent.videoCount,
          progress: updatedContent.progress,
          is_completed: updatedContent.isCompleted,
          tags: updatedContent.tags || []
        });

        if (dbContent) {
          await loadDataFromDB();
          showToast({
            type: 'success',
            title: 'Conteúdo atualizado',
            message: 'O conteúdo foi atualizado com sucesso!'
          });
        }
      } else {
        // Adicionar novo conteúdo
        const newOrder = sessions.find(s => s.id === selectedSessionId)?.contents.length || 0;
        const dbContent = await createContentItem({
          session_id: selectedSessionId,
          title: updatedContent.title,
          description: updatedContent.description,
          image_url: updatedContent.image,
          video_count: updatedContent.videoCount,
          progress: updatedContent.progress,
          is_completed: updatedContent.isCompleted,
          tags: updatedContent.tags || [],
          order_index: newOrder
        });

        if (dbContent) {
          await loadDataFromDB();
          showToast({
            type: 'success',
            title: 'Conteúdo criado',
            message: 'O novo conteúdo foi criado com sucesso!'
          });
        }
      }
    } catch (error) {
      console.error('Erro ao salvar conteúdo:', error);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao salvar conteúdo'
      });
    }

    setShowContentManager(false);
    setEditingContent(null);
    setSelectedSessionId(null);
  };

  const confirmDeleteSession = async () => {
    if (!deletingSession) return;
    
    try {
      const success = await deleteSession(deletingSession.id);
      if (success) {
        await loadDataFromDB();
        showToast({
          type: 'success',
          title: 'Sessão excluída',
          message: 'A sessão foi excluída com sucesso!'
        });
      }
    } catch (error) {
      console.error('Erro ao excluir sessão:', error);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao excluir sessão'
      });
    }
    
    setDeletingSession(null);
  };

  const confirmDeleteContent = async () => {
    if (!deletingContent) return;
    
    try {
      const success = await deleteContentItem(deletingContent.id);
      if (success) {
        await loadDataFromDB();
        showToast({
          type: 'success',
          title: 'Conteúdo excluído',
          message: 'O conteúdo foi excluído com sucesso!'
        });
      }
    } catch (error) {
      console.error('Erro ao excluir conteúdo:', error);
      showToast({
        type: 'error',
        title: 'Erro',
        message: 'Erro ao excluir conteúdo'
      });
    }
    
    setDeletingContent(null);
    setSelectedSessionId(null);
  };

  const handleMoveSession = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sessions.length) return;

    const currentSession = sessions[index];
    const targetSession = sessions[targetIndex];

    let currentIdxValue = currentSession.order;
    let targetIdxValue = targetSession.order;

    if (currentIdxValue === targetIdxValue) {
      currentIdxValue = index;
      targetIdxValue = targetIndex;
    }

    const temp = currentIdxValue;
    currentIdxValue = targetIdxValue;
    targetIdxValue = temp;

    try {
      const updatePromises = [
        updateSession(currentSession.id, { order_index: currentIdxValue }),
        updateSession(targetSession.id, { order_index: targetIdxValue })
      ];
      await Promise.all(updatePromises);
      showToast({
        type: 'success',
        title: 'Ordem Atualizada',
        message: 'A ordem das seções foi atualizada com sucesso.'
      });
      await loadDataFromDB();
    } catch (error) {
      console.error('Erro ao reordenar seções:', error);
      showToast({
        type: 'error',
        title: 'Erro ao Reordenar',
        message: 'Não foi possível salvar a nova ordem.'
      });
    }
  };

  const handleMoveContent = async (sessionId: string, index: number, direction: 'left' | 'right') => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;

    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= session.contents.length) return;

    const currentContent = session.contents[index];
    const targetContent = session.contents[targetIndex];

    try {
      const dbContents = await loadContentItems(sessionId);
      const dbCurrent = dbContents.find(c => c.id === currentContent.id);
      const dbTarget = dbContents.find(c => c.id === targetContent.id);
      if (!dbCurrent || !dbTarget) return;

      let currentIdxValue = dbCurrent.order_index;
      let targetIdxValue = dbTarget.order_index;

      if (currentIdxValue === targetIdxValue) {
        currentIdxValue = index;
        targetIdxValue = targetIndex;
      }

      const temp = currentIdxValue;
      currentIdxValue = targetIdxValue;
      targetIdxValue = temp;

      const updatePromises = [
        updateContentItem(dbCurrent.id, { order_index: currentIdxValue }),
        updateContentItem(dbTarget.id, { order_index: targetIdxValue })
      ];
      await Promise.all(updatePromises);
      showToast({
        type: 'success',
        title: 'Ordem Atualizada',
        message: 'A ordem dos conteúdos foi atualizada com sucesso.'
      });
      await loadDataFromDB();
    } catch (error) {
      console.error('Erro ao reordenar conteúdos:', error);
      showToast({
        type: 'error',
        title: 'Erro ao Reordenar',
        message: 'Não foi possível salvar a nova ordem.'
      });
    }
  };

  const handleStartHere = () => {
    // Redirecionar para página introdutória
    // Aqui você implementaria a navegação para a página introdutória
    console.log('Redirecionando para página introdutória...');
  };

  if (loading) {
    return (
      <div className="flex-1 bg-[#121316] min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#0FFF55]"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#121316] min-h-screen pt-16 md:pt-20">
      {onNavigate && (
        <aside aria-label="Black Challenge" className="mx-auto max-w-screen-2xl px-4 pt-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-[#0FFF55]/25 bg-[#152019] px-4 py-2.5">
            <strong className="text-xs font-extrabold tracking-wider text-[#0FFF55]">BLACK CHALLENGE</strong>
            <p className="flex-auto text-sm text-gray-100">
              Destino do campeão? Medellín e San Andrés. Entre na disputa com suas entregas.
            </p>
            <button
              type="button"
              onClick={() => onNavigate('competicao')}
              className="inline-flex items-center gap-2 rounded-md bg-[#0FFF55] px-3 py-2 text-xs font-bold text-[#101714] hover:bg-[#46ff79] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Quero participar <ArrowRight size={15} aria-hidden="true" />
            </button>
          </div>
        </aside>
      )}
      {/* Header - Banner com VSL e Ecossistema */}
      <div className="relative bg-[#121316] pb-8 sm:pb-10">
        
        <div className="relative px-4 sm:px-6 lg:px-8 pt-6 pb-2 max-w-6xl mx-auto">
          {/* Headline - Texto puro sem borda, apenas renderiza HTML com formatação */}
          <div className="flex items-center justify-center gap-3 mb-6">
            {heroConfig?.headline ? (
              <h2
                className="text-center font-black tracking-widest uppercase text-white text-sm sm:text-base lg:text-lg leading-tight"
                dangerouslySetInnerHTML={{ __html: heroConfig.headline }}
              />
            ) : (
              <h2 className="text-center font-black tracking-widest uppercase text-white text-sm sm:text-base lg:text-lg">
                HEADLINE ECOSSISTEMA MASTERSHOP
              </h2>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowHeroConfigModal(true)}
                className="flex-shrink-0 p-1.5 text-gray-500 hover:text-[#0FFF55] rounded-lg hover:bg-[#0FFF55]/10 transition-all"
                title="Editar Headline, Vídeo e Ícones"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* VSL Video Player Card - Aspecto 16:9 */}
          <div className="relative w-full max-w-4xl mx-auto aspect-video mb-10 rounded-2xl overflow-hidden border border-[#1f2329] bg-[#0f1114] shadow-2xl shadow-black/50">
            {/* Botão de config admin fica acima do player overlay */}
            {isAdmin && (
              <button
                onClick={() => setShowHeroConfigModal(true)}
                className="absolute top-4 right-4 z-20 bg-[#0f1114]/80 backdrop-blur-sm border border-[#1f2329] hover:border-[#0FFF55] text-white p-2.5 rounded-xl hover:scale-105 transition-all shadow-lg"
                title="Configurar Vídeo e Ícones"
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
            <VSLPlayer url={heroConfig?.video_url || ''} />
          </div>

          {/* Badge MASTERSHOP */}
          <div className="flex justify-center mb-6">
            <a
              href={heroConfig?.mastershop_button?.href || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-[#0dd149] text-white text-sm sm:text-base font-black px-8 py-2.5 rounded-full shadow-lg shadow-[#0dd149]/20 hover:bg-[#0ba339] hover:scale-105 transition-all duration-300 uppercase tracking-widest cursor-pointer"
            >
              {heroConfig?.mastershop_button?.name || 'MASTERSHOP'}
            </a>
          </div>

          {/* Ícones de atalho do ecossistema */}
          <div className="flex flex-wrap justify-center gap-5 sm:gap-8 lg:gap-10 py-2 pb-4">
            {heroConfig?.tools.map((tool, index) => (
              <a
                key={index}
                href={tool.href}
                target="_blank"
                rel="noopener noreferrer"
                className="relative flex flex-col items-center group transition-all duration-300 hover:scale-105 mt-2"
              >
                {/* Card escuro com borda verde sutil */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-22 lg:h-22 bg-[#1a1d21] border border-[#0FFF55]/20 group-hover:border-[#0FFF55]/60 rounded-2xl flex items-center justify-center overflow-hidden p-2.5 sm:p-3 shadow-lg group-hover:shadow-[#0FFF55]/10 group-hover:shadow-xl transition-all duration-300">
                  <img
                    src={tool.image_url}
                    alt={tool.name}
                    className="w-full h-full object-contain transition-all duration-300 group-hover:scale-110"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://raw.githubusercontent.com/lojagenericaz/imagens_masterclub/main/images.png';
                    }}
                  />
                </div>

                {/* Badge escura com nome da ferramenta */}
                <div className="absolute -bottom-3 bg-[#0f1114] border border-[#0FFF55]/30 text-[#0FFF55] text-[8px] sm:text-[9px] font-black px-2.5 py-0.5 rounded-full shadow-lg uppercase tracking-widest whitespace-nowrap">
                  {tool.name}
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 xl:px-12 py-6 sm:py-8 max-w-screen-2xl mx-auto">
        {/* Sessões de Conteúdo */}
        {sessions.map((session, index) => (
          <div key={session.id} className="mb-8">
            <div className="mb-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div 
                    className="inline-block text-[#121316] px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-3"
                    style={{ 
                      background: `linear-gradient(to right, ${session.color}, ${session.color}dd)`
                    }}
                  >
                    {session.subtitle}
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2 break-words">
                    {session.title}
                  </h2>
                  <p className="text-gray-400 break-words">
                    {session.description}
                  </p>
                </div>
                
                {/* Botões de gerenciamento da sessão (apenas para admins) */}
                {isAdmin && (
                  <div className="flex items-center space-x-2 flex-shrink-0 flex-wrap gap-2">
                    {index > 0 && (
                      <Button
                        onClick={() => handleMoveSession(index, 'up')}
                        variant="outline"
                        size="sm"
                        className="text-gray-400 hover:text-white border-gray-600 p-2"
                        title="Mover para cima"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                    )}
                    {index < sessions.length - 1 && (
                      <Button
                        onClick={() => handleMoveSession(index, 'down')}
                        variant="outline"
                        size="sm"
                        className="text-gray-400 hover:text-white border-gray-600 p-2"
                        title="Mover para baixo"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      onClick={() => handleEditSession(session)}
                      variant="outline"
                      size="sm"
                      className="text-gray-400 hover:text-white border-gray-600 whitespace-nowrap"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar Sessão
                    </Button>
                    <Button
                      onClick={() => handleDeleteSession(session)}
                      variant="outline"
                      size="sm"
                      className="text-red-400 hover:text-red-300 border-red-600 whitespace-nowrap"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Excluir Sessão
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 w-full justify-items-center">
              {session.contents.map((content, idx) => (
                <ContentCard
                  key={content.id}
                  {...content}
                  isAdmin={isAdmin}
                  onClick={() => handleContentClick(content.id)}
                  onEdit={() => handleEditContent(content, session.id)}
                  onDelete={() => handleDeleteContent(content, session.id)}
                  onMoveLeft={idx > 0 ? () => handleMoveContent(session.id, idx, 'left') : undefined}
                  onMoveRight={idx < session.contents.length - 1 ? () => handleMoveContent(session.id, idx, 'right') : undefined}
                />
              ))}
              
              {/* Card para adicionar novo conteúdo (apenas para admins) */}
              {isAdmin && (
                <div className="relative w-full max-w-[480px] md:max-w-none flex flex-col items-center">
                  {/* Botões de admin para o card de adicionar */}
                  <div className="flex items-center justify-center gap-2 mb-1 w-full">
                    <div className="w-8 h-8"></div> {/* Espaçador para alinhar */}
                    <div className="w-8 h-8"></div> {/* Espaçador para alinhar */}
                  </div>
                  
                  <div
                    onClick={() => handleAddContentToSession(session.id)}
                    className="relative group cursor-pointer overflow-hidden bg-[#121316] rounded-2xl border-2 border-dashed border-[#0FFF55] hover:border-[#0dd149] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#0FFF55]/30 flex items-center justify-center w-full"
                    style={{ aspectRatio: '16/9' }}
                  >
                    <div className="text-center p-4">
                      <div className="w-12 h-12 bg-[#0FFF55] rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#0dd149] transition-colors">
                        <Plus className="w-6 h-6 text-black" />
                      </div>
                      <h3 className="text-white font-semibold text-base mb-1">
                        Adicionar Conteúdo
                      </h3>
                      <p className="text-gray-400 text-xs">
                        Clique para adicionar um novo conteúdo a esta sessão
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Card para adicionar nova sessão (apenas para admins) */}
        {isAdmin && (
          <div className="mb-8">
            <div
              onClick={handleAddNewSession}
              className="relative group cursor-pointer overflow-hidden bg-[#121316] rounded-2xl border-2 border-dashed border-[#0FFF55] hover:border-[#0dd149] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#0FFF55]/30 min-h-[160px] flex items-center justify-center"
            >
              <div className="text-center">
                <div className="w-14 h-14 bg-[#0FFF55] rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-[#0dd149] transition-colors">
                  <Plus className="w-7 h-7 text-black" />
                </div>
                <h3 className="text-white font-bold text-lg mb-1">
                  Adicionar Nova Sessão
                </h3>
                <p className="text-gray-400 text-sm">
                  Clique para criar uma nova sessão de conteúdo
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modais */}
      {heroConfig && (
        <HeroConfigModal
          isOpen={showHeroConfigModal}
          onClose={() => setShowHeroConfigModal(false)}
          currentConfig={heroConfig}
          onSave={handleSaveHeroConfig}
        />
      )}

      {showSessionManager && (
        <SessionManager
          session={editingSession}
          onClose={() => {
            setShowSessionManager(false);
            setEditingSession(null);
          }}
          onSessionUpdated={handleSessionUpdated}
        />
      )}

      {showContentManager && (
        <ContentManager
          content={editingContent}
          sessionId={selectedSessionId}
          bucketName={
            sessions.findIndex(s => s.id === selectedSessionId) !== -1 
              ? `thumbnails_session_${sessions.findIndex(s => s.id === selectedSessionId) + 1}` 
              : 'thumbnails_session_1'
          }
          onClose={() => {
            setShowContentManager(false);
            setEditingContent(null);
            setSelectedSessionId(null);
          }}
          onContentUpdated={handleContentUpdated}
        />
      )}

      {/* Modal de confirmação de exclusão de sessão */}
      <AlertDialog open={!!deletingSession} onOpenChange={() => setDeletingSession(null)}>
        <AlertDialogContent className="bg-[#1a1d23] border-[#1f2329]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Excluir Sessão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Tem certeza que deseja excluir a sessão "{deletingSession?.title}"? 
              Esta ação também excluirá todos os conteúdos da sessão e não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0f1114] border-[#1f2329] text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSession}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de confirmação de exclusão de conteúdo */}
      <AlertDialog open={!!deletingContent} onOpenChange={() => setDeletingContent(null)}>
        <AlertDialogContent className="bg-[#1a1d23] border-[#1f2329]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Excluir Conteúdo
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Tem certeza que deseja excluir o conteúdo "{deletingContent?.title}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0f1114] border-[#1f2329] text-white">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteContent}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HomePage;